import React, { useState, useEffect } from "react";
import { useSignup } from "../hooks/useSignup";
import { Helmet } from "react-helmet";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { FiMail, FiLock, FiCheckCircle } from "react-icons/fi";
import { FaGoogle } from "react-icons/fa";
import AuthShell, { authInputCls, authPrimaryBtn } from "../Components/AuthShell";

const Signup = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { signup, error, success, isLoading } = useSignup()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const next = params.get("next")
  const loginTo = next ? `/login?next=${encodeURIComponent(next)}` : "/login"

  // After a successful signup we can't log the user straight in (email must be
  // verified first), so send them to the login page — carrying `next` so that
  // after they verify + log in they land back where they started (e.g. /redeem).
  useEffect(() => {
    if (!success) return
    const t = setTimeout(() => navigate(loginTo), 3500)
    return () => clearTimeout(t)
  }, [success, loginTo, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    await signup(email, password)
  }

  // Same OAuth entry point as the login page — carry `next` through Google so
  // AppSumo/Gmail users starting at /redeem come back to /redeem after signing up.
  const signupWithGoogle = () => {
    const q = next && next.startsWith("/") ? `?next=${encodeURIComponent(next)}` : ""
    window.open(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/auth/google${q}`, "_self")
  }

  return (
    <AuthShell>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Signup - Content Flash AI</title>
        <meta name="description" content="Build AI-powered forms in seconds with Content Flash AI." />
      </Helmet>

      <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
      <p className="text-sm text-gray-500 mt-1">Start building AI forms free — no card required.</p>

      {success ? (
        <div className="mt-6">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
            <FiCheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">{success}</p>
              <p className="text-xs text-emerald-700 mt-1">
                Redirecting you to the login page… If nothing happens,{" "}
                <Link to={loginTo} className="font-semibold underline">click here to log in</Link>.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <button onClick={signupWithGoogle} className="w-full flex items-center justify-center gap-2 mt-6 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
            <FaGoogle className="text-[#EA4335]" size={15} /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <hr className="flex-grow border-gray-200" />
            <span className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">or</span>
            <hr className="flex-grow border-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={authInputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={authInputCls} />
              </div>
            </div>
            <button disabled={isLoading} className={authPrimaryBtn}>{isLoading ? "Creating…" : "Create account"}</button>
            {error && <div className="text-sm text-center text-red-600 font-medium">{error}</div>}
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account? <Link to={loginTo} className="font-semibold text-indigo-600 hover:text-indigo-700 no-underline">Log in</Link>
          </p>
        </>
      )}
    </AuthShell>
  )
}

export default Signup;
